import { useState, useCallback } from 'react';
import { Check, FlaskConical, Droplets, Calculator, ListChecks } from 'lucide-react';
import InfoBox from '../InfoBox';
import { pHMeter } from '@/components/lab';

interface Part2KaDeterminationProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

// Steps definition
const steps = [
  { id: 1, title: 'Calibrate pH Meter', short: 'Two-point calibration with pH 4 & pH 7 buffers' },
  { id: 2, title: 'Prepare Dilutions', short: 'Make 1.0, 0.5, 0.2, and 0.1 M acetic acid' },
  { id: 3, title: 'Measure pH (1.0 M)', short: 'Insert electrode into 1.0 M solution' },
  { id: 4, title: 'Measure pH (0.5 M)', short: 'Insert electrode into 0.5 M solution' },
  { id: 5, title: 'Measure pH (0.2 M)', short: 'Insert electrode into 0.2 M solution' },
  { id: 6, title: 'Measure pH (0.1 M)', short: 'Insert electrode into 0.1 M solution' },
  { id: 7, title: 'Calculate Ka Values', short: 'Ka = [H⁺]² / (C − [H⁺])' },
  { id: 8, title: 'Average Ka & % Error', short: 'Compare to literature Ka = 1.76 × 10⁻⁵' },
];

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  1: {
    title: 'pH Meter Calibration',
    content: 'Two-point calibration with pH 4 and pH 7 buffers establishes the electrode response. This ensures accurate pH measurements across the acidic range.'
  },
  2: {
    title: 'Prepare Dilution Series',
    content: 'Prepare four acetic acid solutions: 1.0 M (use directly), 0.5 M (10 mL of 1.0 M diluted to 20 mL), 0.2 M (4.0 mL to 20 mL), and 0.1 M (2.0 mL to 20 mL). Place 6.0 mL of each in a clean dry 10 mL beaker for pH measurement.'
  },
  3: {
    title: 'Measuring pH',
    content: 'The pH of a weak acid solution depends on both Ka and the initial concentration. From pH, we can calculate [H⁺] and then determine Ka.',
    chemicalEquation: 'CH₃COOH ⇌ CH₃COO⁻ + H⁺'
  },
  8: {
    title: 'Calculating Ka',
    content: 'For a weak acid: Ka = [H⁺][A⁻]/[HA]. Since [H⁺] = [A⁻] and [HA] ≈ C − [H⁺], we get Ka = [H⁺]²/(C − [H⁺]).',
    chemicalEquation: 'Ka = [H⁺]²/(C − [H⁺])'
  },
  9: {
    title: 'The 5% Rule',
    content: 'If [H⁺]/C < 5%, we can approximate [HA] ≈ C, simplifying calculations. If ionization exceeds 5%, use the quadratic formula for accurate results.'
  },
  10: {
    title: 'Literature Comparison',
    content: 'The literature Ka for acetic acid is 1.76 × 10⁻⁵ (pKa = 4.75). Comparing your experimental value helps assess measurement accuracy.'
  }
};

interface DilutionData {
  concentration: number;
  pH: number | null;
  Ka: number | null;
  valid5Percent: boolean | null;
}

export default function Part2KaDetermination({ onStepComplete, currentStep = 1 }: Part2KaDeterminationProps) {
  // State for info panel
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // State for pH meter calibration
  const [calibrationPoints, setCalibrationPoints] = useState({
    pH4: false,
    pH7: false,
    pH10: false
  });
  const [isCalibrated, setIsCalibrated] = useState(false);

  // State for dilutions
  const [dilutions, setDilutions] = useState<DilutionData[]>([
    { concentration: 1.0, pH: null, Ka: null, valid5Percent: null },
    { concentration: 0.5, pH: null, Ka: null, valid5Percent: null },
    { concentration: 0.2, pH: null, Ka: null, valid5Percent: null },
    { concentration: 0.1, pH: null, Ka: null, valid5Percent: null }
  ]);

  const [dilutionsPrepared, setDilutionsPrepared] = useState(false);
  const [currentMeasurement, setCurrentMeasurement] = useState(0);
  const [averageKa, setAverageKa] = useState<number | null>(null);
  const [percentError, setPercentError] = useState<number | null>(null);

  const LITERATURE_KA = 1.76e-5;

  // Derive step completion
  const stepDone = [
    isCalibrated,
    dilutionsPrepared,
    dilutions[0].pH !== null,
    dilutions[1].pH !== null,
    dilutions[2].pH !== null,
    dilutions[3].pH !== null,
    dilutions[0].Ka !== null,
    averageKa !== null,
  ];
  const completedCount = stepDone.filter(Boolean).length;
  const activeStep = stepDone.findIndex(d => !d) + 1 || steps.length;

  // Calibration handler
  const handleCalibrate = useCallback((buffer: 'pH4' | 'pH7' | 'pH10') => {
    setCalibrationPoints(prev => ({ ...prev, [buffer]: true }));

    // Check if calibrated (need at least pH4 and pH7)
    const newPoints = { ...calibrationPoints, [buffer]: true };
    if (newPoints.pH4 && newPoints.pH7) {
      setIsCalibrated(true);
      if (onStepComplete) onStepComplete('kaDetermination-1');
    }
  }, [calibrationPoints, onStepComplete]);

  const handlePrepareDilutions = useCallback(() => {
    setDilutionsPrepared(true);
    if (onStepComplete) onStepComplete('kaDetermination-2');
  }, [onStepComplete]);

  const handleMeasurepH = useCallback((index: number) => {
    // Simulate pH measurement for acetic acid
    // pH = -log([H+]) where [H+] = sqrt(Ka * C) for weak acids
    const C = dilutions[index].concentration;
    // Add some experimental error
    const error = (Math.random() - 0.5) * 0.1;
    const theoreticalH = Math.sqrt(LITERATURE_KA * C);
    const measuredpH = -Math.log10(theoreticalH) + error;

    setDilutions(prev => {
      const newDilutions = [...prev];
      newDilutions[index] = {
        ...newDilutions[index],
        pH: parseFloat(measuredpH.toFixed(2))
      };
      return newDilutions;
    });

    setCurrentMeasurement(index + 1);

    // Complete step
    const stepMap: Record<number, string> = {
      0: 'kaDetermination-3',
      1: 'kaDetermination-4',
      2: 'kaDetermination-5',
      3: 'kaDetermination-6'
    };
    if (onStepComplete) onStepComplete(stepMap[index]);
  }, [dilutions, onStepComplete]);

  const handleCalculateKa = useCallback(() => {
    // Calculate Ka for each dilution: Ka = [H+]^2 / (C - [H+])
    const newDilutions = dilutions.map(d => {
      if (d.pH === null) return d;
      const H = Math.pow(10, -d.pH);
      const Ka = (H * H) / (d.concentration - H);
      const valid5Percent = (H / d.concentration) < 0.05;
      return {
        ...d,
        Ka: parseFloat(Ka.toExponential(2)),
        valid5Percent
      };
    });

    setDilutions(newDilutions);
    if (onStepComplete) onStepComplete('kaDetermination-8');
  }, [dilutions, onStepComplete]);

  const handleValidate5Percent = useCallback(() => {
    // Already calculated in handleCalculateKa
    if (onStepComplete) onStepComplete('kaDetermination-9');
  }, [onStepComplete]);

  const handleCalculateAverageKa = useCallback(() => {
    // Calculate average Ka from valid measurements
    const validKas = dilutions.filter(d => d.Ka !== null && d.valid5Percent).map(d => d.Ka!);
    if (validKas.length > 0) {
      const avg = validKas.reduce((a, b) => a + b, 0) / validKas.length;
      const error = Math.abs((avg - LITERATURE_KA) / LITERATURE_KA) * 100;
      setAverageKa(parseFloat(avg.toExponential(2)));
      setPercentError(parseFloat(error.toFixed(1)));
      if (onStepComplete) onStepComplete('kaDetermination-10');
    }
  }, [dilutions, onStepComplete]);

  // Beaker component for dilutions
  const DilutionBeaker = ({ data, index, onMeasure }: {
    data: DilutionData;
    index: number;
    onMeasure: () => void;
  }) => (
    <div className={`dilution-beaker ${data.pH !== null ? 'measured' : ''}`}>
      <svg viewBox="0 0 50 60" width={50} height={60}>
        <defs>
          <linearGradient id={`beaker-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
            <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
          </linearGradient>
        </defs>
        <path
          d="M8 10 L8 50 Q8 55 13 55 L37 55 Q42 55 42 50 L42 10"
          fill={`url(#beaker-${index})`}
          stroke="#7A9BB8"
          strokeWidth="1.5"
        />
        <rect x="10" y="25" width="30" height="27" fill="#F5F5DC" opacity="0.6" rx="2" />
      </svg>
      <span className="conc-label">{data.concentration} M</span>
      <span className="ph-value">{data.pH !== null ? `pH ${data.pH}` : '−'}</span>
      {data.pH === null && isCalibrated && dilutionsPrepared && (
        <button className="measure-btn" onClick={onMeasure}>
          Measure
        </button>
      )}
    </div>
  );

  return (
    <div className="exp10-part-layout">
      {/* Steps Panel */}
      <div className="exp10-steps-panel">
        <div className="exp10-steps-header">
          <ListChecks size={16} />
          <h3>Ka Determination</h3>
        </div>
        <div className="exp10-steps-list">
          {steps.map(s => {
            const done = stepDone[s.id - 1];
            const active = s.id === activeStep;
            return (
              <div key={s.id} className={`exp10-step-item ${active && !done ? 'active' : ''}`}>
                <div className={`exp10-step-number ${done ? 'done' : active ? 'current' : ''}`}>
                  {done ? <Check size={12} /> : s.id}
                </div>
                <div className="exp10-step-content">
                  <span className={`exp10-step-title ${active ? 'highlight' : ''}`}>{s.title}</span>
                  {active && !done && <span className="exp10-step-hint">{s.short}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="exp10-lab-bench">
        <div className="exp10-bench-header">
          <div className="exp10-bench-title"><FlaskConical size={16} /><h3>Lab Bench</h3></div>
          <span className="exp10-bench-progress">{completedCount}/{steps.length} Complete</span>
        </div>
        <div className="exp10-bench-area">
      <div className="ka-determination-lab">
        {/* pH Meter Section */}
        <div className="ph-meter-section">
          <pHMeter
            pH={currentMeasurement > 0 && currentMeasurement <= 4 ? dilutions[currentMeasurement - 1].pH || 0 : 7.00}
            temperature={25}
            isCalibrated={isCalibrated}
            electrodeIn={true}
          />
          <div className="calibration-panel">
            <div className="panel-header">
              <h4>Calibration</h4>
              {stepInfoContent[1] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
                >
                  ?
                </button>
              )}
            </div>
            <div className="calibration-buffers">
              <button
                className={`cal-btn ${calibrationPoints.pH4 ? 'done' : ''}`}
                onClick={() => handleCalibrate('pH4')}
                disabled={calibrationPoints.pH4}
              >
                pH 4 {calibrationPoints.pH4 && <Check size={12} />}
              </button>
              <button
                className={`cal-btn ${calibrationPoints.pH7 ? 'done' : ''}`}
                onClick={() => handleCalibrate('pH7')}
                disabled={calibrationPoints.pH7}
              >
                pH 7 {calibrationPoints.pH7 && <Check size={12} />}
              </button>
              <button
                className={`cal-btn ${calibrationPoints.pH10 ? 'done' : ''}`}
                onClick={() => handleCalibrate('pH10')}
                disabled={calibrationPoints.pH10}
              >
                pH 10 {calibrationPoints.pH10 && <Check size={12} />}
              </button>
            </div>
            {isCalibrated && <span className="cal-status">Calibrated</span>}
          </div>
        </div>

        {/* Dilutions Row */}
        <div className="dilutions-section">
          <div className="section-header">
            <h4>Acetic Acid Dilutions</h4>
            {stepInfoContent[2] && (
              <button
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(2); }}
              >
                ?
              </button>
            )}
          </div>
          <div className="dilutions-row">
            {dilutions.map((d, i) => (
              <DilutionBeaker
                key={i}
                data={d}
                index={i}
                onMeasure={() => handleMeasurepH(i)}
              />
            ))}
          </div>
          {!dilutionsPrepared && isCalibrated && (
            <button className="action-btn" onClick={handlePrepareDilutions}>
              <FlaskConical size={16} />
              Prepare Dilutions
            </button>
          )}
        </div>

        {/* Data Table */}
        <div className="data-table">
          <div className="section-header">
            <h4>Ka Calculation Data</h4>
            {stepInfoContent[8] && (
              <button
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(8); }}
              >
                ?
              </button>
            )}
          </div>
          <table>
            <thead>
              <tr>
                <th>Concentration (M)</th>
                <th>pH</th>
                <th>[H⁺]</th>
                <th>Ka</th>
                <th>5% Rule</th>
              </tr>
            </thead>
            <tbody>
              {dilutions.map((d, i) => (
                <tr key={i} className={d.valid5Percent === false ? 'invalid' : ''}>
                  <td>{d.concentration}</td>
                  <td>{d.pH !== null ? d.pH.toFixed(2) : '−'}</td>
                  <td>{d.pH !== null ? Math.pow(10, -d.pH).toExponential(2) : '−'}</td>
                  <td>{d.Ka !== null ? d.Ka.toExponential(2) : '−'}</td>
                  <td className={d.valid5Percent ? 'valid' : d.valid5Percent === false ? 'invalid-cell' : ''}>
                    {d.valid5Percent !== null ? (d.valid5Percent ? 'Valid' : 'Invalid') : '−'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          {dilutions.every(d => d.pH !== null) && !dilutions[0].Ka && (
            <button className="action-btn" onClick={handleCalculateKa}>
              <Calculator size={16} />
              Calculate Ka Values
            </button>
          )}
          {dilutions[0].Ka && !averageKa && (
            <button className="action-btn success" onClick={handleCalculateAverageKa}>
              <Check size={16} />
              Calculate Average Ka
            </button>
          )}
        </div>

        {/* Results */}
        {averageKa && (
          <div className="results-panel">
            <h4>Results</h4>
            <div className="result-grid">
              <div className="result-item">
                <span className="result-label">Average Ka</span>
                <span className="result-value">{averageKa.toExponential(2)}</span>
              </div>
              <div className="result-item">
                <span className="result-label">Literature Ka</span>
                <span className="result-value">{LITERATURE_KA.toExponential(2)}</span>
              </div>
              <div className="result-item">
                <span className="result-label">% Error</span>
                <span className={`result-value ${percentError! < 10 ? 'good' : 'warning'}`}>
                  {percentError}%
                </span>
              </div>
              <div className="result-item">
                <span className="result-label">pKa</span>
                <span className="result-value">{(-Math.log10(averageKa)).toFixed(2)}</span>
              </div>
            </div>
            <div className="formula-box">
              <span>Ka = [H⁺][A⁻] / [HA] = [H⁺]² / (C − [H⁺])</span>
              <span>For CH₃COOH: Ka = 1.76 × 10⁻⁵, pKa = 4.75</span>
            </div>
          </div>
        )}

        <style>{`
          .exp10-part-layout { display: flex; gap: 16px; flex: 1; width: 100%; min-height: 0; padding: 16px; }
          .exp10-steps-panel { width: 240px; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; }
          .exp10-steps-header { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); padding: 14px 16px; display: flex; align-items: center; gap: 10px; color: white; }
          .exp10-steps-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp10-steps-list { flex: 1; overflow-y: auto; padding: 12px; }
          .exp10-step-item { display: flex; align-items: flex-start; gap: 12px; padding: 8px 10px; border-radius: 8px; margin-bottom: 2px; }
          .exp10-step-item.active { background: rgba(255, 90, 54, 0.15); }
          .exp10-step-number { width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; background: var(--border); color: var(--text-muted); }
          .exp10-step-number.done { background: var(--secondary); color: white; }
          .exp10-step-number.current { background: var(--primary); color: white; }
          .exp10-step-content { flex: 1; min-width: 0; }
          .exp10-step-title { font-size: 12px; font-weight: 600; display: block; }
          .exp10-step-title.highlight { color: var(--primary-light); }
          .exp10-step-hint { font-size: 11px; color: var(--text-secondary); margin-top: 4px; display: block; }
          .exp10-lab-bench { flex: 1; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: auto; }
          .exp10-bench-header { background: linear-gradient(135deg, var(--secondary), var(--secondary-dark)); padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; color: white; }
          .exp10-bench-title { display: flex; align-items: center; gap: 10px; }
          .exp10-bench-title h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp10-bench-progress { font-size: 12px; opacity: 0.9; }
          .exp10-bench-area { flex: 1; padding: 20px; overflow-y: auto; }

          .ka-determination-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            width: 100%;
            max-width: 700px;
          }

          .ph-meter-section {
            display: flex;
            gap: 24px;
            align-items: flex-start;
          }

          .calibration-panel {
            background: var(--bg-elevated);
            padding: 12px 16px;
            border-radius: 8px;
          }

          .calibration-panel h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .calibration-buffers {
            display: flex;
            gap: 8px;
          }

          .cal-btn {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 6px 12px;
            background: var(--bg-dark);
            border: 1px solid var(--border);
            border-radius: 6px;
            font-size: 11px;
            color: var(--text-secondary);
            cursor: pointer;
            transition: all 0.2s;
          }

          .cal-btn:hover:not(:disabled) {
            border-color: var(--primary);
          }

          .cal-btn.done {
            background: rgba(0, 168, 107, 0.1);
            border-color: #00A86B;
            color: #00A86B;
          }

          .cal-btn:disabled {
            cursor: default;
          }

          .cal-status {
            display: block;
            margin-top: 8px;
            font-size: 11px;
            color: #00A86B;
            font-weight: 500;
          }

          .dilutions-section {
            width: 100%;
            text-align: center;
          }

          .dilutions-section h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .dilutions-row {
            display: flex;
            justify-content: center;
            gap: 20px;
            margin-bottom: 16px;
          }

          .dilution-beaker {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            padding: 8px;
            background: var(--bg-elevated);
            border-radius: 8px;
            transition: all 0.2s;
          }

          .dilution-beaker.measured {
            border: 1px solid #00A86B;
          }

          .conc-label {
            font-size: 11px;
            font-weight: 600;
            color: var(--text-primary);
          }

          .ph-value {
            font-size: 10px;
            color: var(--text-secondary);
          }

          .measure-btn {
            padding: 4px 8px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 4px;
            font-size: 10px;
            cursor: pointer;
          }

          .measure-btn:hover {
            background: var(--primary-dark);
          }

          .data-table {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 12px;
          }

          .data-table h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }

          th, td {
            padding: 8px;
            text-align: center;
            border: 1px solid var(--border);
          }

          th {
            background: var(--bg-dark);
            font-weight: 600;
            color: var(--text-secondary);
          }

          td {
            color: var(--text-primary);
            font-family: monospace;
          }

          tr.invalid {
            opacity: 0.6;
          }

          .valid {
            color: #00A86B !important;
          }

          .invalid-cell {
            color: #FF5A36 !important;
          }

          .action-buttons {
            display: flex;
            gap: 12px;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .results-panel {
            width: 100%;
            background: rgba(0, 168, 107, 0.1);
            border: 1px solid #00A86B;
            padding: 16px;
            border-radius: 12px;
          }

          .results-panel h4 {
            margin: 0 0 12px 0;
            font-size: 14px;
            color: #00A86B;
          }

          .result-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
            margin-bottom: 16px;
          }

          .result-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .result-label {
            font-size: 10px;
            color: var(--text-muted);
          }

          .result-value {
            font-size: 14px;
            font-weight: 600;
            font-family: monospace;
            color: var(--text-primary);
          }

          .result-value.good {
            color: #00A86B;
          }

          .result-value.warning {
            color: #FFC107;
          }

          .formula-box {
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 12px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 12px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            text-align: center;
          }

          .panel-header, .section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
          }

          .section-header {
            margin-bottom: 12px;
          }

          .section-header h4 {
            margin: 0;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }

          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {/* Info Box */}
        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp10"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
